<template>
	<Dialog
		class="mcp-integration-dialog"
		modal
		:header="integration?.label || 'Integration'"
		:visible="visible"
		@update:visible="emit('update:visible', $event)">
		<div
			v-if="integration"
			class="mcp-integration-dialog__content">
			<div class="mcp-integration-dialog__header">
				<div
					class="mcp-integration-dialog__logo-wrap"
					:style="{ '--integration-color': brandColor }">
					<img
						v-if="showLogo && logoUrl"
						class="mcp-integration-dialog__logo"
						:alt="`${integration.label} logo`"
						:src="logoUrl"
						@error="showLogo = false" />
					<span
						v-else
						class="mcp-integration-dialog__logo-fallback">
						{{ initials }}
					</span>
				</div>
				<div>
					<p class="mcp-integration-dialog__subtitle">Integration Guide</p>
					<p class="mcp-integration-dialog__status">{{ statusText }}</p>
				</div>
			</div>

			<div class="mcp-integration-dialog__actions-row">
				<a
					class="mcp-integration-dialog__docs-link"
					rel="noopener noreferrer"
					target="_blank"
					:href="integration.docs_url">
					Open docs
				</a>
				<a
					v-if="oneClickInstallUrl"
					class="mcp-integration-dialog__one-click-link"
					:href="oneClickInstallUrl">
					One-click install
				</a>
			</div>

			<div class="mcp-integration-dialog__snippet-block">
				<div class="mcp-integration-dialog__snippet-header">
					<span class="mcp-integration-dialog__snippet-title">Setup Snippet</span>
					<Button
						aria-label="Copy setup snippet"
						icon="fa regular fa-copy"
						severity="secondary"
						text
						@click="copyValue(snippet, `${integration.label} snippet copied`)" />
				</div>
				<Message
					v-if="copyErrorMessage"
					severity="error"
					:closable="false">
					{{ copyErrorMessage }}
				</Message>
				<Message
					v-if="copySuccessMessage"
					severity="success"
					:closable="false">
					{{ copySuccessMessage }}
				</Message>
				<pre class="mcp-integration-dialog__snippet"><code>{{ snippet }}</code></pre>
			</div>
		</div>
	</Dialog>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import Button from 'primevue/button'
import Dialog from 'primevue/dialog'
import Message from 'primevue/message'
import type { McpIntegration } from '@/models/Mcp/Types'
import { getIntegrationBranding } from './McpIntegrationAssets'

const props = defineProps<{
	visible: boolean
	integration: McpIntegration | null
	mcpEndpoint: string
}>()

const emit = defineEmits<{
	(e: 'update:visible', value: boolean): void
}>()

const showLogo = ref(true)

const copySuccessMessage = ref<string | null>(null)
const copyErrorMessage = ref<string | null>(null)
let copyMessageTimeoutId: number | null = null

watch(
	() => props.integration?.slug,
	() => {
		showLogo.value = true
	},
)

const logoUrl = computed(() => {
	if (!props.integration) return ''

	return getIntegrationBranding(props.integration.slug)?.logo ?? ''
})

const brandColor = computed(() => {
	if (!props.integration) return '#64748B'

	return getIntegrationBranding(props.integration.slug)?.brandColor ?? '#64748B'
})

const initials = computed(() => {
	if (!props.integration) return '?'

	return props.integration.label
		.split(' ')
		.map((word) => word[0])
		.join('')
		.slice(0, 2)
		.toUpperCase()
})

const statusText = computed(() => {
	if (!props.integration) return ''

	if (props.integration.one_click && props.integration.oauth_client_id) {
		return 'Use one click install or copy the manual config snippet.'
	}

	return 'Copy the config snippet into your config.'
})

const snippet = computed(() => {
	if (!props.integration) return ''

	if (props.integration.snippet_type === 'cursor') {
		const authBlock = props.integration.oauth_client_id
			? `,
      "auth": {
        "CLIENT_ID": "${props.integration.oauth_client_id}",
        "scopes": ["mcp:use"]
      }`
			: ''

		return `{
  "mcpServers": {
    "codecannon": {
      "url": "${props.mcpEndpoint}"${authBlock}
    }
  }
}`
	}

	if (props.integration.snippet_type === 'claude-code') {
		return `claude mcp add --transport http codecannon ${props.mcpEndpoint}`
	}

	if (props.integration.snippet_type === 'opencode') {
		return `{
  "$schema": "https://opencode.ai/config.json",
  "mcp": {
    "codecannon": {
      "type": "remote",
      "url": "${props.mcpEndpoint}"
    }
  }
}`
	}

	return `{
  "mcpServers": {
    "codecannon": {
      "url": "${props.mcpEndpoint}"
    }
  }
}`
})

const oneClickInstallUrl = computed(() => {
	if (!props.integration) return null
	if (props.integration.one_click !== 'cursor') return null

	const auth = props.integration.oauth_client_id
		? {
				CLIENT_ID: props.integration.oauth_client_id,
				scopes: ['mcp:use'],
			}
		: undefined

	const cursorConfig = toBase64(
		JSON.stringify({
			url: props.mcpEndpoint,
			...(auth ? { auth } : {}),
		}),
	)

	return `cursor://anysphere.cursor-deeplink/mcp/install?name=${encodeURIComponent('CodeCannon MCP')}&config=${encodeURIComponent(cursorConfig)}`
})

function toBase64(value: string): string {
	const bytes = new TextEncoder().encode(value)
	let binary = ''

	for (const byte of bytes) {
		binary += String.fromCharCode(byte)
	}

	return btoa(binary)
}

async function copyValue(value: string, successMessage: string) {
	if (!value) return

	try {
		await navigator.clipboard.writeText(value)
		copyErrorMessage.value = null
		copySuccessMessage.value = successMessage

		if (copyMessageTimeoutId) {
			window.clearTimeout(copyMessageTimeoutId)
		}
		copyMessageTimeoutId = window.setTimeout(() => {
			copySuccessMessage.value = null
			copyMessageTimeoutId = null
		}, 2500)
	} catch {
		copySuccessMessage.value = null
		copyErrorMessage.value = 'Failed to copy to clipboard'
	}
}
</script>

<style scoped lang="scss">
.mcp-integration-dialog__content {
	display: flex;
	flex-direction: column;
	gap: 12px;
}

.mcp-integration-dialog__header {
	display: flex;
	align-items: center;
	gap: 12px;
}

.mcp-integration-dialog__logo-wrap {
	--integration-color: #64748b;
	width: 44px;
	height: 44px;
	border-radius: 10px;
	background: color-mix(in srgb, var(--p-surface-800) 84%, var(--integration-color));
	display: grid;
	place-items: center;
	overflow: hidden;
	border: 1px solid color-mix(in srgb, var(--integration-color) 40%, var(--p-content-border-color));
}

.mcp-integration-dialog__logo {
	width: 30px;
	height: 30px;
	object-fit: contain;
}

.mcp-integration-dialog__logo-fallback {
	font-size: 12px;
	font-weight: 700;
	color: var(--p-text-muted-color);
}

.mcp-integration-dialog__subtitle {
	margin: 0;
	font-size: 12px;
	color: var(--p-text-muted-color);
}

.mcp-integration-dialog__status {
	margin: 2px 0 0;
	font-size: 13px;
	font-weight: 600;
}

.mcp-integration-dialog__actions-row {
	display: flex;
	gap: 8px;
	flex-wrap: wrap;
	align-items: center;
}

.mcp-integration-dialog__docs-link,
.mcp-integration-dialog__one-click-link {
	font-size: 12px;
	font-weight: 600;
	text-decoration: none;
	padding: 7px 10px;
	border-radius: 8px;
	border: 1px solid var(--p-content-border-color);
	background: var(--p-surface-800);
	color: var(--p-text-color);
}

.mcp-integration-dialog__one-click-link {
	border-color: color-mix(in srgb, var(--p-primary-color) 50%, var(--p-content-border-color));
	color: var(--p-primary-color);
}

.mcp-integration-dialog__snippet-block {
	display: flex;
	flex-direction: column;
	gap: 6px;
	padding: 10px;
	border-radius: 10px;
	border: 1px solid var(--p-content-border-color);
	background: var(--p-surface-900);
}

.mcp-integration-dialog__snippet-header {
	display: flex;
	justify-content: space-between;
	align-items: center;
	gap: 8px;
}

.mcp-integration-dialog__snippet-title {
	font-size: 12px;
	font-weight: 600;
	color: var(--p-text-muted-color);
}

.mcp-integration-dialog__snippet {
	margin: 0;
	padding: 10px;
	border-radius: 8px;
	border: 1px solid var(--p-content-border-color);
	background: var(--p-surface-950);
	overflow-x: auto;
	font-size: 12px;
	line-height: 1.45;
}
</style>
